// Main-thread compile helpers shared by runtime compile dispatch.
// Example: keep main fallback path consistent with worker fallback path.
export type { LuisMainCompileResult } from './types'
import type { LuisMainCompileResult } from './types'
import type { BuildSourceEntry } from '../loader/process/types'
import { compileWithContext, type LuisCompileContext } from '../loader/context'
import { pushCompileRecoveryWarning } from '../loader/process/compile-recovery'
import { buildLegacyPlaceholderImportRecovery } from '../loader/process/fail-soft-imports'
import { createSerializedImportModeResolver, createSerializedImportResolver } from '../loader/process/import-resolver/serialize'

// Compile one source on the main thread with current runtime context.
export function compileOnMainThread(
  entry: BuildSourceEntry,
  context: LuisCompileContext,
  debugEnabled: boolean,
): LuisMainCompileResult {
  try {
    const compile = compileWithContext(
      entry.rawSource,
      context,
      entry.kind === 'link' ? entry.href : entry.sourceId,
      entry.importResolver,
      entry.importModeResolver,
      entry.compileModeOverride,
      debugEnabled,
    )
    // Return normalized main-thread compile payload.
    return { ...compile, execution: 'main' }
  } catch (error) {
    // Check whether debug mode should keep the original hard failure.
    if (debugEnabled) {
      throw error
    }
    const recovery = buildLegacyPlaceholderImportRecovery(entry.rawSource, entry.serializedImportGraph, error)
    // Check whether graph-wide placeholder recovery is available.
    if (!recovery) {
      throw error
    }

    const compile = compileWithContext(
      recovery._source,
      context,
      entry.kind === 'link' ? entry.href : entry.sourceId,
      createSerializedImportResolver(recovery._serializedImportGraph),
      createSerializedImportModeResolver(entry.serializedImportModes),
      entry.compileModeOverride,
      debugEnabled,
    )
    pushCompileRecoveryWarning(compile.result.warnings, entry.kind === 'link' ? entry.href : entry.sourceId, recovery)
    // Return normalized recovered main-thread compile payload.
    return { ...compile, execution: 'main' }
  }
}
